/**
 * Mock veri deposu — gerçekçi filo fixture'ları.
 *
 * Tüm veriler deterministiktir: kimlikler ve metrik serileri tohumlanmış
 * PRNG ile üretilir, böylece sayfa yenilense bile aynı görünüm korunur.
 * Backend bağlandığında bu dosya tamamen devre dışı kalır
 * (bkz. `src/mocks/index.ts`).
 */
import type {
  Alert,
  AlertRule,
  Device,
  DeviceEvent,
  HardwareInfo,
  InventoryReport,
  MetricName,
  PatchRecord,
  PatchSummaryItem,
  Severity,
  SnmpTarget,
  User,
} from "../api/types";

/* ------------------------------------------------------------------ */
/* Deterministik yardımcılar                                          */
/* ------------------------------------------------------------------ */

function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** [0,1) aralığında deterministik gürültü. */
function noise(key: string): number {
  return mulberry32(hashString(key))();
}

/** Tohumdan UUID biçiminde kimlik üretir (görünüm gerçekçi, tekrarlanabilir). */
function seededId(seed: string): string {
  const rand = mulberry32(hashString(seed));
  const hex = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < 32; i += 1) {
    out += hex[Math.floor(rand() * 16)];
    if (i === 7 || i === 11 || i === 15 || i === 19) out += "-";
  }
  return out;
}

const NOW = Date.now();
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function ago(ms: number): string {
  return new Date(NOW - ms).toISOString();
}

function daysAgo(days: number, jitterKey: string): string {
  return new Date(NOW - days * DAY - Math.floor(noise(jitterKey) * HOUR)).toISOString();
}

/* ------------------------------------------------------------------ */
/* Cihazlar                                                            */
/* ------------------------------------------------------------------ */

interface DeviceSeed {
  hostname: string;
  displayName?: string;
  osFamily: "windows" | "linux";
  osName: string;
  osVersion: string;
  osBuild: string;
  kernel: string;
  site: string;
  tags: string[];
  status: Device["status"];
  lastSeenAgo: number;
  enrolledDaysAgo: number;
  cpuBase: number;
  cpuAmp: number;
  memBase: number;
  diskBase: number;
  netBase: number;
  totalBytes: number;
  cores: number;
  logical: number;
}

const deviceSeeds: DeviceSeed[] = [
  {
    hostname: "srv-dc-01", displayName: "Alan Denetleyicisi", osFamily: "windows",
    osName: "Windows Server 2022", osVersion: "21H2", osBuild: "20348.2402", kernel: "10.0.20348",
    site: "İstanbul-Merkez", tags: ["dc", "prod"], status: "online", lastSeenAgo: 40_000,
    enrolledDaysAgo: 214, cpuBase: 34, cpuAmp: 12, memBase: 62, diskBase: 48, netBase: 260_000,
    totalBytes: 64 * 1024 ** 3, cores: 8, logical: 16,
  },
  {
    hostname: "srv-sql-01", displayName: "SQL Sunucusu", osFamily: "windows",
    osName: "Windows Server 2019", osVersion: "1809", osBuild: "17763.5820", kernel: "10.0.17763",
    site: "İstanbul-Merkez", tags: ["db", "prod"], status: "online", lastSeenAgo: 75_000,
    enrolledDaysAgo: 189, cpuBase: 52, cpuAmp: 18, memBase: 78, diskBase: 71, netBase: 540_000,
    totalBytes: 128 * 1024 ** 3, cores: 16, logical: 32,
  },
  {
    hostname: "srv-file-02", displayName: "Dosya Sunucusu", osFamily: "windows",
    osName: "Windows Server 2016", osVersion: "1607", osBuild: "14393.6981", kernel: "10.0.14393",
    site: "Ankara-Şube", tags: ["file", "prod"], status: "degraded", lastSeenAgo: 18 * MINUTE,
    enrolledDaysAgo: 160, cpuBase: 22, cpuAmp: 9, memBase: 55, diskBase: 92, netBase: 180_000,
    totalBytes: 32 * 1024 ** 3, cores: 4, logical: 8,
  },
  {
    hostname: "ws-muhasebe-07", displayName: "Muhasebe İstemcisi", osFamily: "windows",
    osName: "Windows 11 Pro", osVersion: "23H2", osBuild: "22631.3447", kernel: "10.0.22631",
    site: "İstanbul-Merkez", tags: ["endpoint"], status: "online", lastSeenAgo: 2 * MINUTE,
    enrolledDaysAgo: 96, cpuBase: 18, cpuAmp: 14, memBase: 46, diskBase: 63, netBase: 45_000,
    totalBytes: 16 * 1024 ** 3, cores: 4, logical: 8,
  },
  {
    hostname: "ws-ik-12", displayName: "İK İstemcisi", osFamily: "windows",
    osName: "Windows 10 Pro", osVersion: "22H2", osBuild: "19045.4291", kernel: "10.0.19045",
    site: "Ankara-Şube", tags: ["endpoint"], status: "offline", lastSeenAgo: 7 * HOUR + 20 * MINUTE,
    enrolledDaysAgo: 122, cpuBase: 12, cpuAmp: 8, memBase: 41, diskBase: 57, netBase: 30_000,
    totalBytes: 8 * 1024 ** 3, cores: 2, logical: 4,
  },
  {
    hostname: "ws-depo-03", displayName: "Depo Terminali", osFamily: "windows",
    osName: "Windows 11 Pro", osVersion: "24H2", osBuild: "26100.3194", kernel: "10.0.26100",
    site: "İzmir-Depo", tags: ["endpoint", "depo"], status: "online", lastSeenAgo: 90_000,
    enrolledDaysAgo: 61, cpuBase: 27, cpuAmp: 16, memBase: 52, diskBase: 44, netBase: 62_000,
    totalBytes: 16 * 1024 ** 3, cores: 4, logical: 8,
  },
  {
    hostname: "srv-web-01", displayName: "Web Sunucusu", osFamily: "linux",
    osName: "Ubuntu", osVersion: "24.04 LTS", osBuild: "noble", kernel: "6.8.0-45-generic",
    site: "İstanbul-Merkez", tags: ["web", "prod"], status: "online", lastSeenAgo: 30_000,
    enrolledDaysAgo: 148, cpuBase: 44, cpuAmp: 20, memBase: 58, diskBase: 39, netBase: 780_000,
    totalBytes: 64 * 1024 ** 3, cores: 8, logical: 16,
  },
  {
    hostname: "srv-k8s-node-02", displayName: "K8s Düğümü 2", osFamily: "linux",
    osName: "Ubuntu", osVersion: "22.04 LTS", osBuild: "jammy", kernel: "5.15.0-105-generic",
    site: "İstanbul-Merkez", tags: ["k8s", "prod"], status: "online", lastSeenAgo: 55_000,
    enrolledDaysAgo: 132, cpuBase: 58, cpuAmp: 22, memBase: 74, diskBase: 52, netBase: 1_250_000,
    totalBytes: 96 * 1024 ** 3, cores: 16, logical: 32,
  },
  {
    hostname: "srv-backup-01", displayName: "Yedekleme Sunucusu", osFamily: "linux",
    osName: "Debian", osVersion: "12 (bookworm)", osBuild: "bookworm", kernel: "6.1.0-18-amd64",
    site: "Ankara-Şube", tags: ["backup"], status: "online", lastSeenAgo: 3 * MINUTE,
    enrolledDaysAgo: 176, cpuBase: 21, cpuAmp: 11, memBase: 49, diskBase: 68, netBase: 320_000,
    totalBytes: 48 * 1024 ** 3, cores: 8, logical: 8,
  },
  {
    hostname: "srv-monitor-01", displayName: "İzleme Sunucusu", osFamily: "linux",
    osName: "Rocky Linux", osVersion: "9.4", osBuild: "Blue Onyx", kernel: "5.14.0-427.el9",
    site: "İstanbul-Merkez", tags: ["monitoring", "prod"], status: "degraded", lastSeenAgo: 26 * MINUTE,
    enrolledDaysAgo: 201, cpuBase: 36, cpuAmp: 13, memBase: 66, diskBase: 41, netBase: 210_000,
    totalBytes: 32 * 1024 ** 3, cores: 4, logical: 8,
  },
  {
    hostname: "ws-gelistirici-21", displayName: "Geliştirici İstasyonu", osFamily: "linux",
    osName: "Fedora", osVersion: "40 Workstation", osBuild: "Forty", kernel: "6.9.6-200.fc40.x86_64",
    site: "İstanbul-Merkez", tags: ["endpoint", "dev"], status: "online", lastSeenAgo: 4 * MINUTE,
    enrolledDaysAgo: 44, cpuBase: 31, cpuAmp: 24, memBase: 61, diskBase: 55, netBase: 95_000,
    totalBytes: 24 * 1024 ** 3, cores: 8, logical: 16,
  },
];

export const devices: Device[] = deviceSeeds.map((seed, index) => {
  const id = seededId(`device:${seed.hostname}`);
  return {
    id,
    hostname: seed.hostname,
    displayName: seed.displayName,
    osFamily: seed.osFamily,
    osName: seed.osName,
    osVersion: seed.osVersion,
    osBuild: seed.osBuild,
    kernel: seed.kernel,
    agentVersion: index % 3 === 0 ? "0.3.1" : "0.3.0",
    ips: [
      `10.${20 + (index % 3)}.${Math.floor(index / 3) + 1}.${10 + index}`,
      `192.168.${10 + (index % 4)}.${20 + index}`,
    ],
    macs: [
      `3C:22:FB:${(16 + index).toString(16).toUpperCase()}:7A:${(48 + index).toString(16).toUpperCase()}`,
    ],
    site: seed.site,
    tags: seed.tags,
    status: seed.status,
    lastSeen: ago(seed.lastSeenAgo),
    createdAt: daysAgo(seed.enrolledDaysAgo, `created:${seed.hostname}`),
  };
});

export function findDevice(id: string): Device | undefined {
  return devices.find((device) => device.id === id);
}

export function findDeviceSeed(id: string): DeviceSeed | undefined {
  const index = devices.findIndex((device) => device.id === id);
  return index >= 0 ? deviceSeeds[index] : undefined;
}

/* ------------------------------------------------------------------ */
/* Donanım / envanter                                                  */
/* ------------------------------------------------------------------ */

const cpuModels = [
  "Intel(R) Xeon(R) Gold 6226R @ 2.90GHz",
  "Intel(R) Core(TM) i7-11700 @ 2.50GHz",
  "AMD EPYC 7313P 16-Core Processor",
  "Intel(R) Core(TM) i5-10500 @ 3.10GHz",
  "AMD Ryzen 7 5800X 8-Core Processor",
];
const manufacturers = ["Dell Inc.", "Hewlett-Packard", "Lenovo", "ASUSTeK", "Supermicro"];

export const inventories: InventoryReport[] = devices.map((device, index) => {
  const seed = deviceSeeds[index];
  const rand = mulberry32(hashString(`hw:${device.hostname}`));
  const manufacturer = manufacturers[index % manufacturers.length];
  const memoryBytes = Math.round(seed.totalBytes * (1 + rand() * 0.5));
  const hardware: HardwareInfo = {
    manufacturer,
    model:
      device.osFamily === "windows"
        ? index % 2 === 0
          ? "PowerEdge R740"
          : "ProLiant DL380 Gen10"
        : index % 2 === 0
          ? "ThinkSystem SR630"
          : "RS520+",
    serialNumber: `SN${Math.floor(rand() * 9_000_000 + 1_000_000)}`,
    cpuModel: cpuModels[index % cpuModels.length],
    cpuCores: seed.cores,
    cpuLogical: seed.logical,
    memoryBytes,
    disks:
      device.osFamily === "windows"
        ? [
            { mount: "C:", device: "\\\\.\\PhysicalDrive0", fsType: "NTFS", totalBytes: 512 * 1024 ** 3, usedBytes: Math.round(512 * 1024 ** 3 * (seed.diskBase / 100)) },
            { mount: "D:", device: "\\\\.\\PhysicalDrive1", fsType: "NTFS", totalBytes: 2 * 1024 ** 4, usedBytes: Math.round(2 * 1024 ** 4 * 0.62) },
          ]
        : [
            { mount: "/", device: "/dev/sda1", fsType: "ext4", totalBytes: 512 * 1024 ** 3, usedBytes: Math.round(512 * 1024 ** 3 * (seed.diskBase / 100)) },
            { mount: "/var", device: "/dev/sda2", fsType: "ext4", totalBytes: 1024 ** 4, usedBytes: Math.round(1024 ** 4 * 0.47) },
            { mount: "/boot", device: "/dev/sda3", fsType: "ext4", totalBytes: 2 * 1024 ** 3, usedBytes: Math.round(2 * 1024 ** 3 * 0.31) },
          ],
    nics: [
      {
        name: device.osFamily === "windows" ? "Ethernet0" : "eth0",
        mac: device.macs?.[0],
        ips: [device.ips?.[0] ?? ""],
        speedBps: 1_000_000_000,
        up: device.status !== "offline",
      },
      {
        name: device.osFamily === "windows" ? "Ethernet1" : "eth1",
        mac: device.macs?.[0]?.replace(/:..$/, ":FF"),
        ips: [device.ips?.[1] ?? ""],
        speedBps: 10_000_000_000,
        up: index % 4 !== 0,
      },
    ],
  };
  return {
    deviceId: device.id,
    hostname: device.hostname,
    osFamily: device.osFamily,
    osName: device.osName,
    osVersion: device.osVersion,
    osBuild: device.osBuild,
    kernel: device.kernel,
    agentVersion: device.agentVersion ?? "0.3.0",
    hardware,
    collectedAt: ago(22 * MINUTE + index * MINUTE),
  };
});

export function findInventory(deviceId: string): InventoryReport | undefined {
  return inventories.find((inventory) => inventory.deviceId === deviceId);
}

/* ------------------------------------------------------------------ */
/* Metrik üretimi (deterministik zaman serisi)                         */
/* ------------------------------------------------------------------ */

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Gündüz/gece dalgası: 24 saatlik periyot, 09:00 civarı tepe. */
function diurnal(tMs: number, amplitude: number): number {
  return Math.sin(((tMs / HOUR) * (Math.PI * 2)) / 24 - Math.PI / 2) * amplitude;
}

/**
 * Kümülatif sayaç metrikleri için integral üretir; UI'da delta/dt ile
 * "bytes/s" hızına çevrilir.
 */
function counterValue(
  deviceId: string,
  metric: MetricName,
  tMs: number,
  ratePerSecond: number,
): number {
  const period = 4 * HOUR;
  const phaseKey = `phase:${deviceId}:${metric}`;
  const phase = noise(phaseKey) * Math.PI * 2;
  const base = 1024 ** 3 * (10 + noise(`base:${deviceId}:${metric}`) * 200);
  // rate(t) = r * (1 + 0.45 * sin(2πt/period + phase)) integrali
  const omega = (Math.PI * 2) / period;
  const integral =
    ratePerSecond * (tMs / 1000) +
    ((ratePerSecond * 0.45) / omega) * (Math.cos(phase) - Math.cos(omega * tMs + phase));
  return base + integral;
}

/** Tek bir (cihaz, metrik, an) değeri — mock zaman serisinin çekirdeği. */
export function metricValueAt(
  deviceId: string,
  metric: MetricName,
  tMs: number,
): number {
  const seed = findDeviceSeed(deviceId);
  const jitter = noise(`${deviceId}:${metric}:${Math.floor(tMs / MINUTE)}`) - 0.5;
  const cpuBase = seed?.cpuBase ?? 30;
  const cpuAmp = seed?.cpuAmp ?? 12;
  const memBase = seed?.memBase ?? 55;
  const diskBase = seed?.diskBase ?? 50;
  const netBase = seed?.netBase ?? 150_000;
  const totalBytes = seed?.totalBytes ?? 16 * 1024 ** 3;
  const cores = seed?.cores ?? 4;

  switch (metric) {
    case "cpu.usage_percent":
      return clamp(cpuBase + diurnal(tMs, cpuAmp) + jitter * 9, 1, 99.5);
    case "cpu.load1":
      return clamp(((cpuBase + diurnal(tMs, cpuAmp) + jitter * 9) / 100) * cores * 0.85, 0.05, cores * 2);
    case "memory.usage_percent":
      return clamp(memBase + diurnal(tMs, 4) + jitter * 2.4, 2, 99);
    case "memory.used_bytes":
      return totalBytes * (clamp(memBase + diurnal(tMs, 4) + jitter * 2.4, 2, 99) / 100);
    case "swap.usage_percent":
      return clamp((seed?.memBase ?? 55) * 0.22 + jitter * 3, 0, 100);
    case "disk.usage_percent":
      return clamp(diskBase + diurnal(tMs, 1.2) + jitter * 0.6, 1, 99.5);
    case "disk.read_bytes":
      return counterValue(deviceId, metric, tMs, netBase * 0.35);
    case "disk.write_bytes":
      return counterValue(deviceId, metric, tMs, netBase * 0.22);
    case "net.rx_bytes":
      return counterValue(deviceId, metric, tMs, netBase);
    case "net.tx_bytes":
      return counterValue(deviceId, metric, tMs, netBase * 0.72);
    case "system.uptime_seconds": {
      const bootOffset = 12 * DAY * (0.4 + noise(`boot:${deviceId}`));
      return Math.max(60, tMs / 1000 - (NOW - bootOffset) / 1000);
    }
    case "system.process_count":
      return clamp(180 + cores * 14 + diurnal(tMs, 18) + jitter * 22, 40, 1200);
    default:
      return 0;
  }
}

/* ------------------------------------------------------------------ */
/* Patch kayıtları                                                     */
/* ------------------------------------------------------------------ */

const windowsKbTitles = [
  "Güvenlik Güncelleştirmesi",
  "Aylık Toplu Güncelleştirme",
  ".NET Framework güvenlik güncelleştirmesi",
  "Servis yığını güncelleştirmesi",
  "Kumanda Güvenliği Güncelleştirmesi",
];
const linuxPkgTitles = [
  "kernel güvenlik güncellemesi",
  "openssl güvenlik güncellemesi",
  "glibc kütüphane güncellemesi",
  "curl / libcurl güncellemesi",
  "systemd bileşen güncellemesi",
];
const severities: Severity[] = ["critical", "high", "medium", "low", "unknown"];

function buildPatchRecords(deviceIndex: number, hostname: string, osFamily: "windows" | "linux"): PatchRecord[] {
  const rand = mulberry32(hashString(`patches:${hostname}`));
  const records: PatchRecord[] = [];
  const pendingTarget = [2, 4, 5, 0, 3, 1, 3, 6, 0, 2, 1][deviceIndex % 11];
  const installedTarget = 12 + Math.floor(rand() * 8);

  for (let i = 0; i < installedTarget; i += 1) {
    const identifier =
      osFamily === "windows"
        ? `KB${5000000 + Math.floor(rand() * 400000)}`
        : `${["linux-image-generic", "openssl", "libc6", "curl", "systemd", "openssh-server"][i % 6]}-${(1 + Math.floor(rand() * 4)).toString()}.${Math.floor(rand() * 9)}.${Math.floor(rand() * 9)}-${10 + Math.floor(rand() * 40)}`;
    records.push({
      id: seededId(`patch:${hostname}:installed:${i}`),
      source:
        osFamily === "windows"
          ? i % 3 === 0
            ? "wmi-qfe"
            : "windows-update"
          : ["apt", "dnf", "rpm"][deviceIndex % 3],
      identifier,
      title:
        osFamily === "windows"
          ? windowsKbTitles[i % windowsKbTitles.length]
          : linuxPkgTitles[i % linuxPkgTitles.length],
      severity: severities[Math.floor(rand() * severities.length)],
      status: "installed",
      installedAt: daysAgo(2 + Math.floor(rand() * 210), `inst:${hostname}:${i}`),
      publishedAt: daysAgo(10 + Math.floor(rand() * 300), `pub:${hostname}:${i}`),
      rebootRequired: rand() > 0.88,
    });
  }

  for (let i = 0; i < pendingTarget; i += 1) {
    const identifier =
      osFamily === "windows"
        ? `KB${5030000 + Math.floor(rand() * 90000)}`
        : `${["linux-image-generic", "openssl", "libc6", "python3", "firefox"][i % 5]}-${(1 + Math.floor(rand() * 5)).toString()}.${Math.floor(rand() * 9)}.${Math.floor(rand() * 9)}-${10 + Math.floor(rand() * 50)}`;
    records.push({
      id: seededId(`patch:${hostname}:pending:${i}`),
      source: osFamily === "windows" ? "windows-update" : ["apt", "dnf", "pacman"][deviceIndex % 3],
      identifier,
      title:
        osFamily === "windows"
          ? windowsKbTitles[(i + 2) % windowsKbTitles.length]
          : linuxPkgTitles[(i + 2) % linuxPkgTitles.length],
      severity: i === 0 ? "critical" : severities[Math.floor(rand() * severities.length)],
      status: "pending",
      publishedAt: daysAgo(3 + Math.floor(rand() * 90), `pubp:${hostname}:${i}`),
      rebootRequired: i % 3 === 0,
    });
  }

  if (deviceIndex % 4 === 1) {
    records.push({
      id: seededId(`patch:${hostname}:failed:0`),
      source: osFamily === "windows" ? "windows-update" : "apt",
      identifier: osFamily === "windows" ? "KB5034441" : "grub-efi-amd64-2.06-6",
      title: "Kurulum başarısız oldu — yeniden deneme gerekli",
      severity: "high",
      status: "failed",
      publishedAt: daysAgo(21, `pubf:${hostname}`),
      rebootRequired: false,
    });
  }
  if (deviceIndex % 5 === 2) {
    records.push({
      id: seededId(`patch:${hostname}:hidden:0`),
      source: osFamily === "windows" ? "windows-update" : "dnf",
      identifier: osFamily === "windows" ? "KB5005463" : "kernel-debug-6.1.0",
      title: "Politika gereği gizlendi",
      severity: "low",
      status: "hidden",
      publishedAt: daysAgo(64, `pubh:${hostname}`),
      rebootRequired: false,
    });
  }

  return records;
}

export const patchRecordsByDevice: Map<string, PatchRecord[]> = new Map(
  devices.map((device, index) => [
    device.id,
    buildPatchRecords(index, device.hostname, device.osFamily === "windows" ? "windows" : "linux"),
  ]),
);

export const patchSummaries: PatchSummaryItem[] = devices.map((device) => {
  const records = patchRecordsByDevice.get(device.id) ?? [];
  const pending = records.filter((record) => record.status === "pending");
  const criticalPendingCount = pending.filter((record) => record.severity === "critical").length;
  const oldestPendingDays = pending.reduce((max, record) => {
    if (!record.publishedAt) return max;
    const age = Math.floor((NOW - Date.parse(record.publishedAt)) / DAY);
    return Math.max(max, age);
  }, 0);
  return {
    deviceId: device.id,
    hostname: device.hostname,
    pendingCount: pending.length,
    criticalPendingCount,
    oldestPendingDays,
    lastCheckedAt: ago((3 + (device.id.length % 5)) * HOUR),
  };
});

/* ------------------------------------------------------------------ */
/* Uyarı kuralları ve uyarılar                                         */
/* ------------------------------------------------------------------ */

export const alertRules: AlertRule[] = [
  {
    id: seededId("rule:cpu-critical"),
    name: "Kritik CPU kullanımı",
    metric: "cpu.usage_percent",
    comparison: "gt",
    threshold: 90,
    durationSeconds: 600,
    severity: "critical",
    channels: ["email", "slack-ops"],
    deviceSelector: { site: "İstanbul-Merkez" },
    enabled: true,
  },
  {
    id: seededId("rule:mem-high"),
    name: "Yüksek bellek kullanımı",
    metric: "memory.usage_percent",
    comparison: "gte",
    threshold: 92,
    durationSeconds: 900,
    severity: "high",
    channels: ["email"],
    enabled: true,
  },
  {
    id: seededId("rule:disk-critical"),
    name: "Disk doluluk eşiği",
    metric: "disk.usage_percent",
    comparison: "gt",
    threshold: 95,
    durationSeconds: 300,
    severity: "high",
    channels: ["email", "webhook-ops"],
    enabled: true,
  },
];

interface AlertSeed {
  deviceIndex: number;
  ruleIndex: number;
  state: Alert["state"];
  severity: Severity;
  message: string;
  value: number;
  startedAgo: number;
  resolvedAgo?: number;
  acknowledgedBy?: string;
  acknowledgedAgo?: number;
}

const alertSeeds: AlertSeed[] = [
  {
    deviceIndex: 0, ruleIndex: 0, state: "firing", severity: "critical",
    message: "CPU kullanımı 15 dakikadır %90 üzerinde", value: 96.4, startedAgo: 26 * MINUTE,
  },
  {
    deviceIndex: 1, ruleIndex: 1, state: "acknowledged", severity: "high",
    message: "Bellek kullanımı eşik değerinin üzerinde seyrediyor", value: 94.1,
    startedAgo: 3 * HOUR, acknowledgedBy: "usr-operator", acknowledgedAgo: 42 * MINUTE,
  },
  {
    deviceIndex: 4, ruleIndex: 0, state: "firing", severity: "critical",
    message: "Cihaz 2 saattir yanıt vermiyor (heartbeat yok)", value: 0, startedAgo: 7 * HOUR,
  },
  {
    deviceIndex: 2, ruleIndex: 2, state: "firing", severity: "high",
    message: "C: disk doluluk oranı %95 eşiğini aştı", value: 92.3, startedAgo: 5 * HOUR,
  },
  {
    deviceIndex: 6, ruleIndex: 0, state: "resolved", severity: "medium",
    message: "CPU kullanımı normale döndü", value: 71.2,
    startedAgo: 28 * HOUR, resolvedAgo: 22 * HOUR,
  },
  {
    deviceIndex: 9, ruleIndex: 1, state: "resolved", severity: "low",
    message: "Bellek kullanımı eşiğin altına indi", value: 63.8,
    startedAgo: 50 * HOUR, resolvedAgo: 46 * HOUR,
  },
  {
    deviceIndex: 7, ruleIndex: 2, state: "suppressed", severity: "low",
    message: "/var disk doluluk oranı yükseliyor (bakım penceresi)", value: 77.9, startedAgo: 12 * HOUR,
  },
  {
    deviceIndex: 3, ruleIndex: 0, state: "resolved", severity: "medium",
    message: "Kısa süreli CPU sıçraması", value: 88.5,
    startedAgo: 9 * HOUR, resolvedAgo: 8 * HOUR,
  },
];

export const alerts: Alert[] = alertSeeds.map((seed, index) => {
  const device = devices[seed.deviceIndex];
  return {
    id: seededId(`alert:${index}`),
    ruleId: alertRules[seed.ruleIndex].id,
    deviceId: device.id,
    hostname: device.hostname,
    state: seed.state,
    severity: seed.severity,
    message: seed.message,
    value: seed.value,
    startedAt: ago(seed.startedAgo),
    resolvedAt: seed.resolvedAgo !== undefined ? ago(seed.resolvedAgo) : undefined,
    acknowledgedBy: seed.acknowledgedBy,
    acknowledgedAt:
      seed.acknowledgedAgo !== undefined ? ago(seed.acknowledgedAgo) : undefined,
  };
});

/* ------------------------------------------------------------------ */
/* SNMP hedefleri                                                      */
/* ------------------------------------------------------------------ */

export const snmpTargets: SnmpTarget[] = [
  {
    id: seededId("snmp:core-sw-01"),
    ip: "10.20.0.2",
    port: 161,
    hostname: "core-sw-01.ornek.local",
    sysName: "core-sw-01",
    sysDescr: "Cisco IOS Software, C9300 Software, Version 17.09.4a",
    vendor: "cisco",
    model: "Catalyst C9300-48P",
    version: "v3",
    pollIntervalSeconds: 300,
    status: "online",
    lastSeen: ago(2 * MINUTE),
    createdAt: daysAgo(120, "snmpc:1"),
  },
  {
    id: seededId("snmp:dist-sw-02"),
    ip: "10.20.0.3",
    port: 161,
    hostname: "dist-sw-02.ornek.local",
    sysName: "dist-sw-02",
    sysDescr: "MikroTik RouterOS 7.14.3 (CRS328-24P-4S+)",
    vendor: "mikrotik",
    model: "CRS328-24P-4S+",
    version: "v2c",
    pollIntervalSeconds: 300,
    status: "online",
    lastSeen: ago(4 * MINUTE),
    createdAt: daysAgo(98, "snmpc:2"),
  },
  {
    id: seededId("snmp:ap-kat3-01"),
    ip: "10.20.5.11",
    port: 161,
    hostname: "ap-kat3-01.ornek.local",
    sysName: "ap-kat3-01",
    sysDescr: "Ubiquiti UniFi U6-Pro 6.6.55",
    vendor: "ubiquiti",
    model: "U6-Pro",
    version: "v2c",
    pollIntervalSeconds: 300,
    status: "degraded",
    lastSeen: ago(19 * MINUTE),
    createdAt: daysAgo(64, "snmpc:3"),
  },
  {
    id: seededId("snmp:fw-edge-01"),
    ip: "10.20.0.1",
    port: 161,
    hostname: "fw-edge-01.ornek.local",
    sysName: "fw-edge-01",
    sysDescr: "FortiGate-100F v7.4.3,build2573",
    vendor: "fortinet",
    model: "FortiGate 100F",
    version: "v3",
    pollIntervalSeconds: 600,
    status: "offline",
    lastSeen: ago(26 * HOUR),
    createdAt: daysAgo(150, "snmpc:4"),
  },
];

/* ------------------------------------------------------------------ */
/* Kullanıcılar                                                        */
/* ------------------------------------------------------------------ */

/** Mock kimlik bilgileri — panelde giriş için kullanılır. */
export const MOCK_PASSWORD = "Demo!Passw0rd";

export const users: User[] = [
  {
    id: "usr-admin",
    email: "admin@demo.local",
    displayName: "Ayşe Demir",
    role: "admin",
    mfaEnabled: true,
    createdAt: daysAgo(240, "user:admin"),
    lastLoginAt: ago(2 * HOUR),
  },
  {
    id: "usr-operator",
    email: "operator@demo.local",
    displayName: "Mert Yılmaz",
    role: "operator",
    mfaEnabled: false,
    createdAt: daysAgo(180, "user:operator"),
    lastLoginAt: ago(26 * HOUR),
  },
  {
    id: "usr-viewer",
    email: "viewer@demo.local",
    displayName: "Zeynep Kaya",
    role: "viewer",
    mfaEnabled: false,
    createdAt: daysAgo(90, "user:viewer"),
    lastLoginAt: ago(5 * DAY),
  },
];

/* ------------------------------------------------------------------ */
/* Cihaz olay zaman çizelgesi (GET /devices/{id}/events)               */
/* ------------------------------------------------------------------ */

/**
 * Cihaz başına gerçekçi olay akışı: enrollment, envanter değişikliği,
 * online/offline geçişleri, yama kurulumları ve uyarı yaşam döngüsü.
 */
export const deviceEvents: DeviceEvent[] = devices.flatMap((device, index) => {
  const entries: DeviceEvent[] = [];
  const seed = deviceSeeds[index];
  const push = (
    type: DeviceEvent["type"],
    message: string,
    at: string,
    extra?: { severity?: Severity; refId?: string },
  ) => {
    entries.push({
      id: seededId(`event:${device.id}:${type}:${message}:${at}`),
      deviceId: device.id,
      type,
      message,
      at,
      severity: extra?.severity,
      refId: extra?.refId,
    });
  };

  // Kayıt (enrollment) + ilk envanter
  push("enrollment", `Cihaz kaydı tamamlandı — agent ${device.agentVersion ?? "0.3.0"}`, device.createdAt);
  push(
    "inventory",
    "İlk envanter anlık görüntüsü toplandı",
    new Date(Date.parse(device.createdAt) + 42 * MINUTE).toISOString(),
  );

  // Durum geçişleri
  if (device.status === "offline") {
    push("status", "Cihaz çevrimiçi oldu", ago(seed.lastSeenAgo + 3 * HOUR));
    push("status", "Heartbeat kesildi — cihaz çevrimdışı", ago(seed.lastSeenAgo), {
      severity: "critical",
    });
  } else if (device.status === "degraded") {
    push("status", "Cihaz durumu 'bozuk' olarak işaretlendi (sağlık kontrolü)", ago(seed.lastSeenAgo), {
      severity: "medium",
    });
  } else {
    push("status", "Cihaz çevrimiçi oldu", ago(seed.lastSeenAgo + 26 * HOUR));
    push("status", "Cihaz çevrimiçi oldu", ago(seed.lastSeenAgo));
  }

  // Yaşam döngüsü: agent güncellemesi (bazı cihazlarda)
  if (index % 3 === 0) {
    push(
      "inventory",
      `Agent sürümü güncellendi: ${device.agentVersion ?? "0.3.0"}`,
      daysAgo(12 + index, `agent-upd:${device.id}`),
    );
  }

  // Yama olayları (en yeni kurulumlar + bekleyenler)
  const records = patchRecordsByDevice.get(device.id) ?? [];
  for (const record of records.filter((r) => r.status === "installed").slice(-3)) {
    if (!record.installedAt) continue;
    push("patch", `Yama kuruldu: ${record.identifier}`, record.installedAt, {
      refId: record.id,
    });
    if (record.rebootRequired) {
      push(
        "patch",
        `Yeniden başlatma bekleniyor: ${record.identifier}`,
        new Date(Date.parse(record.installedAt) + 20 * MINUTE).toISOString(),
        { severity: "medium", refId: record.id },
      );
    }
  }
  for (const record of records.filter((r) => r.status === "pending").slice(0, 2)) {
    if (!record.publishedAt) continue;
    push("patch", `Bekleyen yama bildirildi: ${record.identifier}`, record.publishedAt, {
      severity: record.severity,
      refId: record.id,
    });
  }
  const failed = records.find((r) => r.status === "failed");
  if (failed?.publishedAt) {
    push("patch", `Yama kurulumu başarısız: ${failed.identifier}`, failed.publishedAt, {
      severity: "high",
      refId: failed.id,
    });
  }

  // Uyarı yaşam döngüsü
  for (const alert of alerts.filter((a) => a.deviceId === device.id)) {
    push("alert", `Uyarı tetiklendi: ${alert.message}`, alert.startedAt, {
      severity: alert.severity,
      refId: alert.id,
    });
    if (alert.resolvedAt) {
      push("alert", `Uyarı çözüldü: ${alert.message}`, alert.resolvedAt, {
        refId: alert.id,
      });
    }
    if (alert.acknowledgedAt) {
      push("alert", `Uyarı onaylandı: ${alert.message}`, alert.acknowledgedAt, {
        refId: alert.id,
      });
    }
  }

  return entries;
});